import { useMemo, useState } from 'react'
import { Check, Loader2, UserPlus, X } from 'lucide-react'
import { useStaff } from '@/api/hooks'
import type { components } from '@/api/schema'
import { StaffAvatar } from '@/components/staff-avatar'
import {
  PickerMenu,
  PickerMenuContent,
  PickerMenuItem,
  PickerMenuList,
  PickerMenuSearch,
  PickerMenuSeparator,
  PickerMenuTrigger,
} from '@/components/picker-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

export type StaffAssignmentUser = components['schemas']['UserRead']

/**
 * Shared Project Details-style owner picker. The menu is portalled so it is
 * safe inside sticky and overflow containers, while the trigger retains the
 * compact avatar-pill treatment used throughout the dashboard.
 */
export function StaffAssignmentPicker({
  label,
  currentUser,
  currentUserId,
  allowedRoles,
  disabled = false,
  pending = false,
  showName = false,
  attention = false,
  onChange,
  className,
}: {
  label: string
  currentUser?: StaffAssignmentUser | null
  currentUserId?: number | null
  allowedRoles?: string[]
  disabled?: boolean
  pending?: boolean
  showName?: boolean
  /** Highlights the person as owning open action items. */
  attention?: boolean
  onChange: (user: StaffAssignmentUser | null) => void
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const staff = useStaff()
  const normalizedRoles = useMemo(
    () => new Set((allowedRoles ?? []).map((role) => role.toLowerCase())),
    [allowedRoles],
  )
  const eligible = useMemo(
    () =>
      (staff.data ?? []).filter(
        (user) => normalizedRoles.size === 0 || normalizedRoles.has((user.role ?? '').toLowerCase()),
      ),
    [normalizedRoles, staff.data],
  )
  const selected = currentUser ?? eligible.find((user) => user.id === currentUserId) ?? null
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return eligible
    return eligible.filter(
      (user) =>
        user.name.toLowerCase().includes(query) || user.email.toLowerCase().includes(query),
    )
  }, [eligible, search])

  const tooltip = selected ? `${label}: ${selected.name}` : `Assign ${label}`
  const description = attention ? `${tooltip}, has open action items` : tooltip

  const choose = (user: StaffAssignmentUser | null) => {
    setOpen(false)
    setSearch('')
    onChange(user)
  }

  return (
    <TooltipProvider>
      <PickerMenu
        open={open}
        onOpenChange={(nextOpen) => {
          if (!pending) setOpen(nextOpen)
          if (!nextOpen) setSearch('')
        }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <PickerMenuTrigger asChild disabled={disabled || pending}>
              <button
                type="button"
                aria-label={description}
                aria-busy={pending}
                className={cn(
                  'inline-flex min-h-9 max-w-64 items-center gap-2 rounded-full border bg-background px-1.5 pr-3 text-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-60',
                  !selected && 'border-dashed bg-muted/40',
                  attention &&
                    'border-emerald-200 bg-emerald-50 hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950/40',
                  className,
                )}
              >
                <StaffAvatar
                  size="sm"
                  name={selected?.name}
                  photoUrl={pending ? null : selected?.photo_url}
                  className={cn(selected && 'bg-primary text-primary-foreground')}
                  fallbackClassName={cn(selected && 'bg-primary text-primary-foreground')}
                >
                  {pending ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : selected ? undefined : (
                    <UserPlus className="size-3.5" />
                  )}
                </StaffAvatar>
                <span className="truncate text-foreground/70">{label}</span>
                {showName && selected ? <span className="truncate font-medium">{selected.name}</span> : null}
              </button>
            </PickerMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" sideOffset={6}>
            {tooltip}
            {attention ? <span className="block opacity-80">Has open action items</span> : null}
          </TooltipContent>
        </Tooltip>

        <PickerMenuContent title={`Assign ${label}`} className="w-80">
          <PickerMenuSearch
            value={search}
            onChange={setSearch}
            placeholder="Search staff"
            ariaLabel={`Search ${label} staff`}
          />
          <PickerMenuList className="max-h-64">
            {selected ? (
              <>
                <PickerMenuItem onSelect={() => choose(null)} className="min-h-10 text-muted-foreground">
                  <X className="size-4" /> Unassign {label}
                </PickerMenuItem>
                <PickerMenuSeparator />
              </>
            ) : null}
            {staff.isLoading ? (
              <div className="flex min-h-20 items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" /> Loading staff
              </div>
            ) : filtered.length ? (
              filtered.map((user) => {
                const isCurrent = selected?.id === user.id
                return (
                  <PickerMenuItem
                    key={user.id}
                    disabled={isCurrent}
                    onSelect={() => choose(user)}
                    className="min-h-11 justify-between"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <StaffAvatar size="sm" name={user.name} photoUrl={user.photo_url} />
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{user.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">{user.email}</span>
                      </span>
                    </span>
                    {isCurrent ? (
                      <Check className="size-4 shrink-0 text-primary" />
                    ) : user.role ? (
                      <span className="shrink-0 text-xs text-muted-foreground">{user.role}</span>
                    ) : null}
                  </PickerMenuItem>
                )
              })
            ) : (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">No matching staff</p>
            )}
          </PickerMenuList>
        </PickerMenuContent>
      </PickerMenu>
    </TooltipProvider>
  )
}
